// =============================================================================
// negotiation-client.http.ts — Adaptateur HTTP RÉEL du NegotiationClient (S18 / stream B).
// =============================================================================
// Câble la SPA admin contre les surfaces GELÉES du stream B (NegotiationController). Substitué derrière le MÊME
// DI seam (NegotiationClientProvider) : écrans/hooks consomment l'interface NegotiationClient, l'impl est
// substituée sans toucher l'UI (stub-first ; cet adaptateur câble le réseau).
//
// AUTORITÉ = API/worker (NestJS, AdminSessionGuard, garde per-profil profilesAuthorized, transitions de
// participation, verrouillage final). Cet adaptateur ne porte AUCUNE règle métier : il sérialise l'intention,
// parse la réponse, mappe l'erreur (message serveur tel quel).
//
// SÉCURITÉ (RULE_REF_006, parité rank-admin-client.http.ts) : le secret de session admin (cookie httpOnly) n'est
// JAMAIS lu ni fabriqué ici ; il est transmis par credentials: 'include'. Le GET (état) n'exige AUCUN en-tête
// CSRF ; les MUTATIONS (reopen / lock) ré-émettent le cookie NON-httpOnly `admin_csrf` en en-tête
// `x-csrf-token` (double-submit). L'auteur de la réouverture/du verrouillage est server-set (session). Le
// campaignId est la ressource d'URL.

import {
  NegotiationApiError,
  type LockNegotiationResultDto,
  type NegotiationAuthStateDto,
  type NegotiationCandidatesDto,
  type NegotiationClient,
  type NegotiationComparisonDto,
  type LockSelectionResultDto,
  type NegotiationLinesDto,
  type NegotiationHistoryDto,
  type NegotiationHistoryQueryInput,
  type ReopenNegotiationInput,
  type ReopenNegotiationResultDto,
} from '@/api/negotiation-client';

/** Forme attendue d'un corps d'erreur structuré (NestJS HttpException). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly message?: string;
}

/** Nom du cookie CSRF NON-httpOnly posé au login admin (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'admin_csrf';

/** Lit la valeur du cookie CSRF `admin_csrf` (NON-httpOnly). `undefined` si absent (fail-closed côté serveur). */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel : `fetch` contre l'API admin négociation. `baseUrl` par défaut vide (chemins relatifs).
 * En test, on injecte un baseUrl ou on mocke `fetch`.
 */
export class HttpNegotiationClient implements NegotiationClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  /** Exécute une requête et renvoie le JSON typé, ou lève une NegotiationApiError. CSRF double-submit sur POST. */
  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers: Record<string, string> = {
      'content-type': 'application/json',
    };
    if (method !== 'GET' && method !== 'HEAD') {
      const csrf = readCsrfToken();
      if (csrf !== undefined) {
        headers['x-csrf-token'] = csrf;
      }
    }
    const response = await fetch(`${this.baseUrl}${path}`, {
      credentials: 'include',
      ...init,
      headers: { ...headers, ...init.headers },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /** Convertit une réponse non-OK en erreur applicative typée (message serveur tel quel). */
  private async toApiError(response: Response): Promise<NegotiationApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur la négociation.`;
    return new NegotiationApiError(message, response.status);
  }

  public getCandidates(
    campaignId: string,
  ): Promise<NegotiationCandidatesDto> {
    return this.request<NegotiationCandidatesDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/negotiation/candidates`,
      { method: 'GET' },
    );
  }

  public getAuthState(campaignId: string): Promise<NegotiationAuthStateDto> {
    return this.request<NegotiationAuthStateDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/negotiation/auth`,
      { method: 'GET' },
    );
  }

  public reopen(
    campaignId: string,
    input: ReopenNegotiationInput,
  ): Promise<ReopenNegotiationResultDto> {
    // Contrat API (autorité) : `{ targets: [{ supplierSiret, profilesAuthorized: string[] }] }` — un
    // TABLEAU de cibles, `profilesAuthorized` = liste blanche de CODES de profil (le guard back
    // `isProfileAuthorized` teste l'appartenance du profileCode ; les niveaux sont ré-attachés au read via
    // le ProfileScope). L'UI réouvre UN fournisseur à la fois ⇒ tableau à 1 cible. On mappe les couples
    // sélectionnés (profileCode × niveau) vers les CODES DISTINCTS attendus par l'API (drift de contrat
    // corrigé : le client envoyait un objet unique {supplierSiret, profilesAuthorized:[objets]} → 400).
    // A2 — `mode` est relayé TEL QUEL quand l'appelant l'exprime (`'add'` pour l'ouverture par binôme depuis
    // le ranking) ; OMIS sinon, l'API retombant sur son défaut historique `'replace'`. L'UI ne décide pas :
    // elle relaie l'intention, l'API reste l'autorité.
    const profileCodes = [...new Set(input.profilesAuthorized.map((p) => p.profileCode))];
    const body = {
      targets: [
        {
          supplierSiret: input.supplierSiret,
          profilesAuthorized: profileCodes,
          ...(input.mode !== undefined ? { mode: input.mode } : {}),
        },
      ],
    };
    return this.request<ReopenNegotiationResultDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/negotiation/reopen`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  public lock(
    campaignId: string,
    supplierSiret?: string,
  ): Promise<LockNegotiationResultDto> {
    // Corps VIDE `{}` ⇒ verrouillage GLOBAL (campagne par son id d'URL + admin server-set). Corps
    // `{supplierSiret}` ⇒ verrouillage INDIVIDUEL de ce fournisseur (DBU l.1264, item 34b) : le back
    // discrimine sur la présence de `supplierSiret` (SIRET 14 chiffres validé côté API).
    const body =
      supplierSiret === undefined ? {} : { supplierSiret };
    return this.request<LockNegotiationResultDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/negotiation/lock`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  public lockSelection(
    campaignId: string,
    supplierSirets: readonly string[],
  ): Promise<LockSelectionResultDto> {
    // LOT N2 — UNE seule requête pour toute la sélection (jamais N appels de verrouillage individuel :
    // l'API doit pouvoir pré-vérifier le lot ENTIER avant d'écrire, ce qu'une boucle client empêcherait).
    // On relaie la liste telle quelle : l'API dédoublonne et reste l'autorité (cohorte, traçabilité).
    // MUTATION ⇒ CSRF double-submit (posé par `request`).
    return this.request<LockSelectionResultDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/negotiation/lock-selection`,
      { method: 'POST', body: JSON.stringify({ supplierSirets: [...supplierSirets] }) },
    );
  }

  public getNegotiationComparison(
    campaignId: string,
    supplierSiret: string,
  ): Promise<NegotiationComparisonDto> {
    // GET (lecture) : aucun CSRF. `supplierSiret` en query (le back valide 14 chiffres → 400 sinon).
    // Passthrough intégral : l'UI ne recalcule NI ne réordonne les cellules (autorité = read-model back).
    return this.request<NegotiationComparisonDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/negotiation/comparison` +
        `?supplierSiret=${encodeURIComponent(supplierSiret)}`,
      { method: 'GET' },
    );
  }

  public getNegotiationLines(campaignId: string): Promise<NegotiationLinesDto> {
    // LOT N1 — UNE seule requête pour toute la page (grain Fournisseur × Profil × Séniorité). GET
    // (lecture) : aucun CSRF. Passthrough INTÉGRAL : l'UI ne recalcule NI l'écart, NI l'état, NI le tri
    // (autorité = read-model back). Le seul dérivé laissé au front est le renfort « Attente 48 h+ ».
    return this.request<NegotiationLinesDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/negotiation/lines`,
      { method: 'GET' },
    );
  }

  public getNegotiationHistory(
    campaignId: string,
    query: NegotiationHistoryQueryInput = {},
  ): Promise<NegotiationHistoryDto> {
    // LOT N3 — UNE seule requête. GET (lecture) : aucun CSRF. Les paramètres ABSENTS ne sont PAS envoyés
    // (les défauts sont ceux du serveur : Tous / page 1 / 50) — envoyer une valeur par défaut côté client
    // en ferait une seconde source de vérité. Passthrough INTÉGRAL : l'UI ne retrie NI ne refiltre.
    const params = new URLSearchParams();
    if (query.filter !== undefined) {
      params.set('type', query.filter);
    }
    if (query.page !== undefined) {
      params.set('page', String(query.page));
    }
    if (query.pageSize !== undefined) {
      params.set('pageSize', String(query.pageSize));
    }
    const suffix = params.size === 0 ? '' : `?${params.toString()}`;
    return this.request<NegotiationHistoryDto>(
      `/admin/campaigns/${encodeURIComponent(campaignId)}/negotiation/history${suffix}`,
      { method: 'GET' },
    );
  }
}
